import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import BottomNavigation from '../components/BottomNavigation';
import ShareButtons from '../components/ShareButtons';
import { ArrowLeft, MapPin, Phone, Mail, User, Tag, Calendar, ChevronLeft, ChevronRight, CheckCircle2, AlertTriangle, ArrowLeftRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const ClassifiedDetail = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const editToken = searchParams.get('token');
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPhoto, setCurrentPhoto] = useState(0);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [changingStatus, setChangingStatus] = useState(false);
  const [showOfferForm, setShowOfferForm] = useState(false);
  const [swapOffers, setSwapOffers] = useState([]);

  // Owner: load swap offers on their own swap/sell_swap listing.
  useEffect(() => {
    if (!editToken || !item) return;
    const lt2 = item.listingType || 'sale';
    if (lt2 !== 'swap' && lt2 !== 'sell_swap') return;
    axios.get(`${API}/api/classifieds/${id}/swap-offers?token=${editToken}`)
      .then(r => setSwapOffers(r.data.offers || []))
      .catch(() => {});
  }, [editToken, item, id]);

  useEffect(() => {
    axios.get(`${API}/api/classifieds/${id}`)
      .then(res => setItem(res.data))
      .catch(() => navigate('/classifieds'))
      .finally(() => setLoading(false));
  }, [id, navigate]);

  const handleStatusChange = async (newStatus) => {
    if (!editToken) return;
    setChangingStatus(true);
    try {
      await axios.put(`${API}/api/classifieds/${id}/status?token=${editToken}&status=${newStatus}`);
      setItem(prev => ({ ...prev, status: newStatus }));
    } catch (err) {
      alert(t('classifieds.errorStatus', 'Could not update status.'));
    } finally {
      setChangingStatus(false);
    }
  };

  const handleDelete = async () => {
    if (!editToken) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/api/classifieds/${id}?token=${editToken}`);
      navigate('/classifieds');
    } catch (err) {
      alert(t('classifieds.errorDelete', 'Could not delete listing.'));
    } finally {
      setDeleting(false);
    }
  };

  const categoryIcons = {
    'Tools': '🔧', 'Furniture': '🪑', 'Cars & Vehicles': '🚗', 'Electronics': '📱',
    'Property': '🏠', 'Clothing & Fashion': '👗', 'Home & Garden': '🌿',
    'Sports & Leisure': '⚽', 'Baby & Kids': '👶', 'General': '📦'
  };

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
    } catch { return ''; }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pb-24">
        <div className="animate-pulse max-w-2xl mx-auto px-4 pt-6">
          <div className="h-8 bg-gray-200 rounded w-32 mb-4"></div>
          <div className="h-64 bg-gray-200 rounded-xl mb-4"></div>
          <div className="h-6 bg-gray-200 rounded w-3/4 mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
        </div>
        <BottomNavigation />
      </div>
    );
  }

  if (!item) return null;

  const photos = item.photos || [];
  const isSold = item.status === 'sold';
  const isPending = item.status === 'pending_collection';
  const isSwapped = item.status === 'swapped';
  const lt = item.listingType || 'sale';
  const isSellSwap = lt === 'sell_swap';
  const isSwapOnly = lt === 'swap';
  const isSwap = isSellSwap || isSwapOnly;
  const swapWanted = item.swapWanted || [];

  const statusLabels = {
    active: { label: t('classifieds.statusAvailable', 'Available'), color: 'bg-emerald-100 text-emerald-700' },
    pending_collection: { label: t('classifieds.statusPending', 'Pending Collection'), color: 'bg-amber-100 text-amber-700' },
    sold: { label: t('classifieds.statusSold', 'Sold'), color: 'bg-red-100 text-red-700' },
    swapped: { label: t('classifieds.statusSwapped', 'Swapped!'), color: 'bg-teal-100 text-teal-700' },
  };
  // Owner status options depend on the listing type.
  const ownerStatuses = ['active', 'pending_collection',
    ...((lt === 'sale' || lt === 'sell_swap') ? ['sold'] : []),
    ...(isSwap ? ['swapped'] : [])];

  const contactSubject = encodeURIComponent(`Find-2-Day: ${item.title}`);
  const buyBody = encodeURIComponent(`Hi ${item.contactName || ''},\n\nI'm interested in buying "${item.title}"${item.price != null ? ` (€${item.price})` : ''} on Find-2-Day. Is it still available?\n\nThanks`);
  const swapBody = encodeURIComponent(`Hi ${item.contactName || ''},\n\nI'd like to make a SWAP OFFER for "${item.title}" on Find-2-Day.\n\nWhat I'm offering in exchange:\n- (describe your item, condition, any cash difference)\n\nThanks`);

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Top bar */}
      <header className="bg-white border-b sticky top-0 z-30">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
          <button onClick={() => navigate('/classifieds')} className="flex items-center gap-1 text-gray-600 hover:text-gray-900" data-testid="back-to-classifieds">
            <ArrowLeft className="h-5 w-5" />
            <span className="text-sm font-medium">{t('classifieds.title', 'Sell / Lost / Found')}</span>
          </button>
          <ShareButtons
            url={window.location.href}
            title={`${item.title} - ${item.price !== null ? `€${item.price}` : ''}`}
            size="sm"
          />
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-4">
        {/* Photo gallery */}
        {photos.length > 0 ? (
          <div className="relative rounded-xl overflow-hidden bg-gray-100 mb-4">
            <img
              src={photos[currentPhoto]}
              alt={`${item.title} - photo ${currentPhoto + 1}`}
              className="w-full aspect-[4/3] object-cover"
              data-testid="classified-main-photo"
            />
            {(isSold || isPending || isSwapped) && (
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                <span className={`px-6 py-2 rounded-full text-lg font-bold uppercase tracking-wide ${
                  isSold ? 'bg-red-500 text-white' : isSwapped ? 'bg-teal-500 text-white' : 'bg-amber-400 text-amber-900'
                }`}>
                  {isSold ? t('classifieds.sold', 'Sold') : isSwapped ? t('classifieds.statusSwapped', 'Swapped!') : t('classifieds.statusPending', 'Pending Collection')}
                </span>
              </div>
            )}
            {photos.length > 1 && (
              <>
                <button
                  onClick={() => setCurrentPhoto(p => p > 0 ? p - 1 : photos.length - 1)}
                  className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-colors"
                  data-testid="photo-prev"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <button
                  onClick={() => setCurrentPhoto(p => p < photos.length - 1 ? p + 1 : 0)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-colors"
                  data-testid="photo-next"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                  {photos.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setCurrentPhoto(i)}
                      className={`w-2 h-2 rounded-full transition-colors ${i === currentPhoto ? 'bg-white' : 'bg-white/50'}`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="rounded-xl bg-gradient-to-br from-emerald-50 to-teal-50 flex items-center justify-center aspect-[16/10] mb-4">
            <span className="text-6xl">{categoryIcons[item.category] || '📦'}</span>
          </div>
        )}

        {/* Thumbnail strip */}
        {photos.length > 1 && (
          <div className="flex gap-2 mb-4 overflow-x-auto pb-1">
            {photos.map((photo, i) => (
              <button
                key={i}
                onClick={() => setCurrentPhoto(i)}
                className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-colors ${
                  i === currentPhoto ? 'border-emerald-500' : 'border-transparent'
                }`}
              >
                <img src={photo} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        )}

        {/* Price + Status */}
        <div className="flex items-center gap-3 mb-2">
          <div className="text-2xl font-bold text-gray-900" data-testid="classified-detail-price">
            {isSwapOnly ? (
              <span className="inline-flex items-center gap-2 text-teal-700"><ArrowLeftRight className="h-6 w-6" />{t('marketplace.swapLabel', 'Swap')}</span>
            ) : (item.price !== null && item.price !== undefined ? (
              <span>€{item.price.toFixed(2)}{isSellSwap && <span className="text-teal-700 text-lg font-extrabold ml-2">{t('marketplace.orSwap', 'OR SWAP')}</span>}</span>
            ) : t('classifieds.negotiable', 'Negotiable'))}
          </div>
          {(isSold || isPending || isSwapped) && (
            <span className={`px-3 py-1 text-xs font-bold rounded-full uppercase ${statusLabels[item.status]?.color}`} data-testid="status-badge">
              {statusLabels[item.status]?.label}
            </span>
          )}
        </div>

        {/* Swap details block */}
        {isSwap && (
          <div className="bg-teal-50 border border-teal-200 rounded-xl p-4 mb-3" data-testid="swap-detail-block">
            {isSellSwap && item.price != null && (
              <p className="text-sm text-teal-900 font-semibold" data-testid="swap-buy-for">{t('classifieds.buyFor', 'Buy for')}: €{item.price.toFixed(2)}</p>
            )}
            <p className="text-sm text-teal-900 mt-1" data-testid="swap-wanted">
              <span className="font-semibold">{t('classifieds.swapWantedShort', 'Swap wanted')}:</span>{' '}
              {swapWanted.length ? swapWanted.join(', ') : t('classifieds.swapOpenAny', 'open to ideas')}
            </p>
            {item.openToOffers !== false && (
              <p className="text-xs text-teal-700/80 mt-1" data-testid="swap-other-offers">{t('classifieds.otherOffersConsidered', 'Other offers considered')}</p>
            )}
          </div>
        )}

        {/* Title */}
        <h1 className="text-xl font-semibold text-gray-900 mb-3" data-testid="classified-detail-title">{item.title}</h1>

        {/* Category & date */}
        <div className="flex items-center gap-3 mb-4 text-sm text-gray-500">
          <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-emerald-50 text-emerald-700 rounded-full text-xs font-medium">
            <Tag className="h-3 w-3" />
            {t(`classifieds.cat.${item.category}`, item.category)}
          </span>
          <span className="inline-flex items-center gap-1">
            <Calendar className="h-3.5 w-3.5" />
            {formatDate(item.createdAt)}
          </span>
        </div>

        {/* OWNER CONTROLS - shown prominently right after title when manage link is used */}
        {editToken && (
          <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border-2 border-emerald-300 rounded-xl p-5 mb-5" data-testid="owner-controls-panel">
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle2 className="h-5 w-5 text-emerald-600" />
              <h2 className="text-base font-bold text-gray-900">{t('classifieds.manageYourListing', 'Manage Your Listing')}</h2>
            </div>

            {/* Status selector */}
            <p className="text-sm font-medium text-gray-700 mb-2">{t('classifieds.changeStatus', 'Change Status')}:</p>
            <div className="flex flex-wrap gap-2 mb-4">
              {ownerStatuses.map((s) => (
                <button
                  key={s}
                  onClick={() => item.status !== s && handleStatusChange(s)}
                  disabled={changingStatus}
                  className={`flex-1 min-w-[80px] px-2 py-3 rounded-lg text-[13px] font-bold transition-colors border-2 ${
                    item.status === s
                      ? s === 'active' ? 'bg-emerald-600 text-white border-emerald-600 shadow-md'
                        : s === 'pending_collection' ? 'bg-amber-500 text-white border-amber-500 shadow-md'
                        : s === 'swapped' ? 'bg-teal-600 text-white border-teal-600 shadow-md'
                        : 'bg-red-500 text-white border-red-500 shadow-md'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400 hover:bg-gray-50'
                  } disabled:opacity-50`}
                  data-testid={`status-btn-${s}`}
                >
                  {statusLabels[s]?.label}
                </button>
              ))}
            </div>

            {item.status === 'sold' && (
              <p className="text-xs text-gray-500 mb-3">{t('classifieds.soldAutoRemove', 'Sold listings are hidden from search and auto-removed after 3 days.')}</p>
            )}
            {item.status === 'swapped' && (
              <p className="text-xs text-gray-500 mb-3">{t('classifieds.swappedNote', 'Swapped listings are hidden from search. Your record is kept for you.')}</p>
            )}

            {/* Section 8 — change listing type / price on the SAME listing */}
            <TypePriceEditor item={item} listingId={id} editToken={editToken} onSaved={(patch) => setItem(prev => ({ ...prev, ...patch }))} t={t} />


            <button
              onClick={() => setShowConfirmDelete(true)}
              className="w-full px-4 py-2.5 bg-red-100 text-red-700 rounded-lg font-semibold hover:bg-red-200 transition-colors text-sm border border-red-200"
              data-testid="delete-listing-btn"
            >
              {t('classifieds.deleteListing', 'Delete Listing')}
            </button>
          </div>
        )}

        {/* Description */}
        <div className="bg-white rounded-xl p-4 mb-4 shadow-sm border border-gray-100">
          <h2 className="text-sm font-semibold text-gray-900 mb-2">{t('classifieds.description', 'Description')}</h2>
          <p className="text-sm text-gray-700 whitespace-pre-line leading-relaxed" data-testid="classified-detail-desc">{item.description}</p>
        </div>

        {/* Location */}
        {item.location && (
          <div className="bg-white rounded-xl p-4 mb-4 shadow-sm border border-gray-100">
            <div className="flex items-center gap-2 text-gray-700">
              <MapPin className="h-4 w-4 text-emerald-600 flex-shrink-0" />
              <span className="text-sm" data-testid="classified-detail-location">{item.location}</span>
            </div>
          </div>
        )}

        {/* Buy / Swap actions — Phase 1 reuses the existing email contact.
            (Phase 2 will replace MAKE A SWAP OFFER with the in-app negotiation.) */}
        {!editToken && !isSold && !isSwapped && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4" data-testid="marketplace-actions">
            {(lt === 'sale' || lt === 'sell_swap') && (
              <a href={`mailto:${item.contactEmail}?subject=${contactSubject}&body=${buyBody}`}
                 className="flex items-center justify-center gap-2 px-4 py-3 bg-emerald-600 text-white rounded-lg font-semibold hover:bg-emerald-700 transition-colors text-sm"
                 data-testid="contact-to-buy-btn">
                <Mail className="h-4 w-4" />{t('classifieds.contactToBuy', 'Contact to Buy')}
              </a>
            )}
            {isSwap && (
              <button onClick={() => setShowOfferForm(v => !v)}
                 className="flex items-center justify-center gap-2 px-4 py-3 bg-teal-600 text-white rounded-lg font-semibold hover:bg-teal-700 transition-colors text-sm"
                 data-testid="make-swap-offer-btn">
                <ArrowLeftRight className="h-4 w-4" />{t('classifieds.makeSwapOffer', 'Make a Swap Offer')}
              </button>
            )}
          </div>
        )}

        {/* In-app swap offer form (Phase 2) */}
        {!editToken && isSwap && showOfferForm && (
          <SwapOfferForm listingId={id} listingTitle={item.title} t={t}
            onCancel={() => setShowOfferForm(false)}
            onCreated={(res) => navigate(`/swap/${res.offerId}?token=${res.offererToken}`)} />
        )}

        {/* Owner: offers received on this swap listing */}
        {editToken && isSwap && (
          <div className="bg-white rounded-xl p-4 mb-4 shadow-sm border border-gray-100" data-testid="owner-swap-offers">
            <h2 className="text-sm font-semibold text-gray-900 mb-2">{t('classifieds.offersReceived', 'Swap offers received')} ({swapOffers.length})</h2>
            {swapOffers.length === 0 ? (
              <p className="text-xs text-gray-500" data-testid="owner-no-offers">{t('classifieds.noOffersYet', 'No swap offers yet.')}</p>
            ) : (
              <div className="space-y-2">
                {swapOffers.map(o => {
                  const cur = (o.thread || [])[o.thread.length - 1] || {};
                  return (
                    <button key={o.id} onClick={() => navigate(`/swap/${o.id}?token=${editToken}`)}
                      className="w-full text-left rounded-lg border border-teal-200 bg-teal-50 p-2.5 hover:bg-teal-100 transition-colors" data-testid={`owner-offer-${o.id}`}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-medium text-teal-900 truncate">{o.offererName}</span>
                        <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-teal-200 text-teal-800">{o.status}</span>
                      </div>
                      <p className="text-xs text-teal-700 mt-0.5 truncate">{(cur.bProvides || []).join(', ')}{cur.money ? ` + €${cur.money}` : ''}</p>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Contact details */}
        <div className="bg-white rounded-xl p-4 mb-4 shadow-sm border border-gray-100">
          <h2 className="text-sm font-semibold text-gray-900 mb-3">{t('classifieds.contactSeller', 'Contact Seller')}</h2>
          <div className="space-y-2.5">
            <div className="flex items-center gap-2 text-sm text-gray-700">
              <User className="h-4 w-4 text-emerald-600 flex-shrink-0" />
              <span data-testid="classified-contact-name">{item.contactName}</span>
            </div>
            <a href={`mailto:${item.contactEmail}`} className="flex items-center gap-2 text-sm text-emerald-700 hover:text-emerald-800" data-testid="classified-contact-email">
              <Mail className="h-4 w-4 flex-shrink-0" />
              {item.contactEmail}
            </a>
            {item.contactPhone && (
              <a href={`tel:${item.contactPhone}`} className="flex items-center gap-2 text-sm text-emerald-700 hover:text-emerald-800" data-testid="classified-contact-phone">
                <Phone className="h-4 w-4 flex-shrink-0" />
                {item.contactPhone}
              </a>
            )}
          </div>
        </div>

        {/* Expiry info */}
        {item.expiresAt && !isSold && !editToken && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4 flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-amber-700">
              {t('classifieds.expiresOn', 'This listing expires on')} {formatDate(item.expiresAt)}
            </p>
          </div>
        )}

      </div>

      {/* Delete confirmation modal */}
      {showConfirmDelete && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="confirm-delete-modal">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('classifieds.confirmDelete', 'Delete Listing?')}</h3>
            <p className="text-sm text-gray-600 mb-4">{t('classifieds.confirmDeleteDesc', 'This will permanently remove your listing. This action cannot be undone.')}</p>
            <div className="flex gap-2">
              <button onClick={() => setShowConfirmDelete(false)} className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 text-sm">
                {t('cancel', 'Cancel')}
              </button>
              <button onClick={handleDelete} disabled={deleting}
                className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 disabled:opacity-50 text-sm"
                data-testid="confirm-delete-btn">
                {deleting ? t('classifieds.deleting', 'Deleting...') : t('classifieds.yesDelete', 'Yes, Delete')}
              </button>
            </div>
          </div>
        </div>
      )}

      <BottomNavigation />
    </div>
  );
};

// Section 8 — owner edits listing type / price / swap prefs on the SAME listing
// (keeps id, photos, description, views, messages, owner, history).
const TYPE_OPTS = [
  { key: 'sale', label: 'Sell only' },
  { key: 'swap', label: 'Swap only' },
  { key: 'sell_swap', label: 'Sell or Swap' },
  { key: 'free', label: 'Free' },
  { key: 'wanted', label: 'Wanted' },
];
const TypePriceEditor = ({ item, listingId, editToken, onSaved, t }) => {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState(item.listingType || 'sale');
  const [price, setPrice] = useState(item.price != null ? String(item.price) : '');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const showPrice = type === 'sale' || type === 'sell_swap';
  const isSwap = type === 'swap' || type === 'sell_swap';

  const save = async () => {
    setErr('');
    if (type === 'sell_swap' && (!price || parseFloat(price) <= 0)) {
      setErr(t('classifieds.errSellSwapPrice', 'Please enter a selling price for a "Sell or Swap" listing'));
      return;
    }
    setSaving(true);
    try {
      const payload = { listingType: type };
      if (showPrice && price) payload.price = parseFloat(price);
      const res = await axios.put(`${API}/api/classifieds/${listingId}?token=${editToken}`, payload);
      onSaved({ listingType: type, price: showPrice && price ? parseFloat(price) : null });
      setOpen(false);
    } catch (e) {
      setErr(e.response?.data?.detail || t('classifieds.errSave', 'Could not save changes.'));
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)}
        className="w-full mb-3 px-4 py-2.5 bg-teal-100 text-teal-800 rounded-lg font-semibold hover:bg-teal-200 transition-colors text-sm border border-teal-200"
        data-testid="edit-type-price-btn">
        {t('classifieds.editTypePrice', 'Change listing type / price (e.g. also sell this item)')}
      </button>
    );
  }
  return (
    <div className="mb-3 p-3 rounded-lg bg-white border border-teal-200 space-y-2" data-testid="type-price-editor">
      <p className="text-sm font-semibold text-gray-800">{t('classifieds.listingType', 'Listing type')}</p>
      <div className="grid grid-cols-2 gap-2">
        {TYPE_OPTS.map(o => (
          <button key={o.key} type="button" onClick={() => setType(o.key)}
            className={`px-2 py-2 rounded-lg border text-[13px] font-medium ${type === o.key ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-gray-700 border-gray-200'}`}
            data-testid={`edit-type-${o.key}`}>
            {t(`classifieds.type${o.key === 'sale' ? 'SellOnly' : o.key === 'swap' ? 'SwapOnly' : o.key === 'sell_swap' ? 'SellOrSwap' : o.key === 'free' ? 'Free' : 'Wanted'}`, o.label)}
          </button>
        ))}
      </div>
      {showPrice && (
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">{t('classifieds.price', 'Price')} {type === 'sell_swap' ? '*' : ''}</label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">€</span>
            <input type="number" min="0" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)}
              className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-teal-500"
              data-testid="edit-price-input" />
          </div>
        </div>
      )}
      {isSwap && (
        <p className="text-xs text-teal-700/80">{t('classifieds.editSwapNote', 'Buyers will see “€… OR SWAP” and can make a swap offer. Your photos, description and listing ID stay the same.')}</p>
      )}
      {err && <p className="text-xs text-red-600" data-testid="edit-type-price-error">{err}</p>}
      <div className="flex gap-2">
        <button onClick={save} disabled={saving} className="flex-1 px-3 py-2 bg-teal-600 text-white rounded-lg text-sm font-semibold disabled:opacity-50" data-testid="save-type-price-btn">
          {saving ? t('classifieds.saving', 'Saving...') : t('save', 'Save')}
        </button>
        <button onClick={() => setOpen(false)} className="flex-1 px-3 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium" data-testid="cancel-type-price-btn">
          {t('cancel', 'Cancel')}
        </button>
      </div>
    </div>
  );
};

// Phase 2 — buyer's initial in-app swap offer (owner item is fixed = listing).
const SwapOfferForm = ({ listingId, listingTitle, t, onCancel, onCreated }) => {
  const { i18n } = useTranslation();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [items, setItems] = useState('');
  const [money, setMoney] = useState('');
  const [dir, setDir] = useState('b_to_a');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const submit = async () => {
    setErr('');
    const bProvides = items.split(',').map(x => x.trim()).filter(Boolean);
    if (name.trim().length < 2) return setErr(t('swap.errName', 'Please enter your name.'));
    if (!email.includes('@')) return setErr(t('swap.errEmail', 'Please enter a valid email.'));
    if (!bProvides.length) return setErr(t('swap.errItems', 'Describe at least one item you are offering.'));
    setBusy(true);
    try {
      const res = await axios.post(`${API}/api/classifieds/${listingId}/swap-offers`, {
        offererName: name.trim(), offererEmail: email.trim(), offererPhone: phone.trim() || null,
        bProvides, money: money ? parseFloat(money) : 0,
        moneyDirection: money && parseFloat(money) > 0 ? dir : 'none', note: note.trim(),
        lang: i18n.language,
      });
      onCreated(res.data);
    } catch (e) {
      setErr(e.response?.data?.detail || t('swap.createFail', 'Could not send offer.'));
    } finally { setBusy(false); }
  };
  return (
    <div className="bg-teal-50 border border-teal-200 rounded-xl p-4 mb-4 space-y-3" data-testid="swap-offer-form">
      <p className="text-sm font-semibold text-teal-900">🔄 {t('classifieds.makeSwapOffer', 'Make a Swap Offer')} — {listingTitle}</p>
      <p className="text-xs text-teal-700/80">{t('swap.ownerGives', 'The owner gives')}: <strong>{listingTitle}</strong>. {t('swap.describeYours', 'Describe what you would give in exchange (you can add money too).')}</p>
      <div className="grid grid-cols-2 gap-2">
        <input value={name} onChange={e => setName(e.target.value)} placeholder={t('classifieds.yourName', 'Name')} className="px-3 py-2 border border-gray-300 rounded-lg text-sm" data-testid="offer-name-input" />
        <input value={email} onChange={e => setEmail(e.target.value)} placeholder={t('classifieds.yourEmail', 'Email')} className="px-3 py-2 border border-gray-300 rounded-lg text-sm" data-testid="offer-email-input" />
      </div>
      <input value={phone} onChange={e => setPhone(e.target.value)} placeholder={t('classifieds.yourPhone', 'Phone (optional)')} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" data-testid="offer-phone-input" />
      <input value={items} onChange={e => setItems(e.target.value)} placeholder={t('swap.itemsPlaceholder', 'e.g. Lawnmower, Strimmer (comma separated)')} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" data-testid="offer-items-input" />
      <div className="grid grid-cols-2 gap-2">
        <input type="number" min="0" step="0.01" value={money} onChange={e => setMoney(e.target.value)} placeholder={t('swap.moneyAmount', 'Money (€)')} className="px-3 py-2 border border-gray-300 rounded-lg text-sm" data-testid="offer-money-input" />
        <select value={dir} onChange={e => setDir(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white" data-testid="offer-dir-select">
          <option value="b_to_a">{t('swap.dirBtoA', 'I pay the owner')}</option>
          <option value="a_to_b">{t('swap.dirAtoB', 'Owner pays me')}</option>
        </select>
      </div>
      <input value={note} onChange={e => setNote(e.target.value)} placeholder={t('swap.notePlaceholder', 'Optional note…')} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" data-testid="offer-note-input" />
      {err && <p className="text-xs text-red-600" data-testid="offer-error">{err}</p>}
      <div className="flex gap-2">
        <button disabled={busy} onClick={submit} className="flex-1 py-2.5 bg-teal-600 text-white rounded-lg font-semibold text-sm" data-testid="offer-send-btn">{busy ? t('classifieds.saving', 'Saving...') : t('swap.sendOffer', 'Send swap offer')}</button>
        <button disabled={busy} onClick={onCancel} className="flex-1 py-2.5 bg-white text-gray-700 rounded-lg font-medium text-sm border" data-testid="offer-cancel-btn">{t('cancel', 'Cancel')}</button>
      </div>
    </div>
  );
};

export default ClassifiedDetail;